import Alpine from "alpinejs";
import { handleCustomerUserErrors, handleStorefrontErrors, storefront } from "./storefront";
import { AddLineItemData, cart } from "./cart";
import { cloud } from "./cloud";
import { CUSTOMER_EVENTS, GLOBAL_EVENTS, CART_EVENTS, KLAVIYO_EVENTS } from "./events";
import {
  CUSTOMER_ACCESS_TOKEN_CREATE_MUTATION,
  CUSTOMER_RECOVER_MUTATION,
  CUSTOMER_UPDATE_MUTATION,
  CustomerAccessTokenCreateMutation,
  CustomerRecoverMutation,
  CustomerUpdateMutation,
} from "@app/gql/mutations";
import { getCurrentStoreConfig } from "@app/config";

// Customer Actions

type UpdateCustomerDetailsData = {
  firstName?: string;
  lastName?: string;
  birthDay?: string;
  birthMonth?: string;
  phone?: string;
};

function handleCustomerDetailsUpdateResponse(response?: UpdateCustomerResponse[]) {
  if (!response) return;
  const errors = response
    .filter((res) => res?.data?.customerUpdate)
    .reduce((acc: string[], res) => {
      const errors = res?.data?.customerUpdate?.userErrors;
      if (!errors?.length) return acc;
      const message = errors.map((error) => `${error.message}`).join("");
      acc.push(message);
      return acc;
    }, []);
  if (!errors.length) return;
  throw errors.join("\n");
}

type UpdateCustomerResponse = {
  data: {
    customerUpdate: {
      userErrors: { message: string; field: string[] }[];
    };
  };
};

async function updateCustomerDetails(data: UpdateCustomerDetailsData) {
  const response = await cloud.post<UpdateCustomerResponse[], any>("/customer/update", data);
  handleCustomerDetailsUpdateResponse(response);
  window.location.reload();
}

type UpdateCustomerPreferencesData = {
  recommendations?: boolean;
  promotions?: boolean;
  member?: boolean;
  sms?: boolean;
  email?: boolean;
  unsubscribe?: boolean;
  customer_tags: string[];
};

const CUSTOMER_PREFERENCES: { [key: string]: string } = {
  recommendations: "preference:recommendations",
  promotions: "preference:new-releases",
  member: "preference:membership",
  email: "preference:email",
  sms: "preference:sms",
  unsubscribe: "preference:unsubscribe",
} as const;

async function updateCustomerPreferences(data: UpdateCustomerPreferencesData) {
  const tags = Object.keys(data).reduce((acc: string[], key: string) => {
    // @ts-ignore
    const isKeyChecked = data[key];
    if (!isKeyChecked) return acc;

    const tag = CUSTOMER_PREFERENCES[key];
    if (!tag) return acc;

    acc.push(tag);
    return acc;
  }, []);

  const response = await cloud.post("/customer/preferences/update", { tags });
  window.location.reload();
  return response;
}

// Storefront API Actions

type UpdateCustomerLoginData = {
  oldEmail?: string;
  oldPassword?: string;
  newEmail?: string;
  newPassword?: string;
};

const handleSuccessfulResponse = (object: any) => {
  const message = window.SHOPIFY_DATA?.locales?.account?.update_details_success ?? "Updated Succesfully.";
  const updatedValues = Object.keys(object);
  setTimeout(() => {
    window.location.reload();
  }, 5000);
  return `${updatedValues.join(",")} ${message}`;
};

async function updateCustomerLogin(data: UpdateCustomerLoginData) {
  if (!data.newEmail) throw "Email address can not be blank";

  if (data.newEmail !== data.oldEmail) {
    const confirmation = confirm(
      "It looks like the new email address provided does not match your previous one. Are you sure you want to proceed?",
    );
    if (!confirmation) return;
  }

  const customerAccessTokenCreateResponse = await storefront.mutate<CustomerAccessTokenCreateMutation>({
    mutation: CUSTOMER_ACCESS_TOKEN_CREATE_MUTATION,
    variables: {
      input: {
        email: data.oldEmail,
        password: data.oldPassword,
      },
    },
  });

  const { customerAccessTokenCreate } = await handleStorefrontErrors(customerAccessTokenCreateResponse);
  await handleCustomerUserErrors(customerAccessTokenCreate.customerUserErrors);
  const { customerAccessToken } = customerAccessTokenCreate;

  const customer = {
    ...(data?.newEmail ? { email: data.newEmail } : {}),
    ...(data?.newPassword ? { password: data.newPassword } : {}),
  };

  const customerUpdateResponse = await storefront.mutate<CustomerUpdateMutation>({
    mutation: CUSTOMER_UPDATE_MUTATION,
    variables: {
      customerAccessToken: customerAccessToken.accessToken,
      customer,
    },
  });

  const { customerUpdate } = await handleStorefrontErrors(customerUpdateResponse);
  await handleCustomerUserErrors(customerUpdate.customerUserErrors);
  return handleSuccessfulResponse(customer);
}

type CustomerRecoverData = {
  email: string;
};

async function recoverCustomer(data: CustomerRecoverData) {
  const customerRecoverResponse = await storefront.mutate<CustomerRecoverMutation>({
    mutation: CUSTOMER_RECOVER_MUTATION,
    variables: {
      email: data.email,
    },
  });

  const { customerRecover } = await handleStorefrontErrors(customerRecoverResponse);
  await handleCustomerUserErrors(customerRecover.customerUserErrors);

  // By default if the customer resent email is set the customer will be
  // redirect to the /pages/recovery-sent. If the customer resent email is not set
  window.location.href = `/pages/recovery-sent?recovery_email=${data.email}`;
}

// Cart Actions

type AddToCartData = {
  variantId?: number;
  membership_price: number;
};

async function addToCart(data: AddToCartData) {
  if (!data.variantId) throw "Please select a size";
  await cart.addLineItems({
    id: data.variantId,
    quantity: 1,
    properties: {
      "_x-membership-price": (data.membership_price ?? 0.0).toString(),
    },
  });
}

type KlaviyoBackInStockNotifyData = {
  email: string;
  variant: string;
};

async function klaviyoBackInStockNotify(data: KlaviyoBackInStockNotifyData) {
  const config = getCurrentStoreConfig();
  const klaviyoCompanyId = config.klaviyo.id;
  const klaviyoApiVersion = config.klaviyo.apiVersion;

  if (!klaviyoCompanyId || !klaviyoApiVersion) throw "";

  const { email, variant } = data;
  const payload = {
    data: {
      type: "back-in-stock-subscription",
      attributes: {
        profile: {
          data: {
            type: "profile",
            attributes: { email },
          },
        },
        channels: ["EMAIL"],
      },
      relationships: {
        variant: {
          data: {
            type: "catalog-variant",
            id: `$shopify:::$default:::${variant}`,
          },
        },
      },
    },
  };

  const response = await fetch(
    `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoCompanyId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        revision: klaviyoApiVersion,
      },
      body: JSON.stringify(payload),
    },
  );

  return true;
}

type AddGiftCardToCartData = {
  message?: string;
  sender_name?: string;
  sender_email?: string;
  recipient_name?: string;
  recipient_email?: string;
  selected_card?: string;
  selected_card_image?: string;
  variantId?: number;
};

async function addGiftCardToCart(data: AddGiftCardToCartData) {
  if (!data.variantId) throw "Please select a variant";

  const item = {
    id: data.variantId,
    quantity: 1,
    properties: {
      From: data.sender_name,
      To: data.recipient_name,
      Email: data.recipient_email,
      Message: data.message,
      Design: data.selected_card,
      _Image: data.selected_card_image,
      _UniqueId: Math.random().toString(36).substring(2, 15),
    },
  };

  await cart.addLineItems(item);
}

type AddLookItemsData = {
  [key: string]: string;
};

async function addLookItems(data: AddLookItemsData) {
  const selectedIds = Object.values(data);
  const items = selectedIds.reduce((acc: AddLineItemData[], selectedId) => {
    if (!selectedId) return acc;
    const id = parseInt(selectedId);
    acc.push({
      id,
      quantity: 1,
    });
    return acc;
  }, []);
  if (!items?.length) throw "Please select a variant";
  const response = await cart.addLineItems(items);
}

type AddLookItemData = {
  submit: {
    productId?: string;
  };
} & {
  [key: `product_${string}`]: string;
};

async function addLookItem(data: AddLookItemData) {
  const productIdKey = data.submit?.productId;
  const variant = data[`product_${productIdKey}`];
  if (!variant) throw "Please select a variant";
  const id = parseInt(variant);
  await cart.addLineItems({
    id,
    quantity: 1,
  });
}

type GiftCardBalanceData = {
  number: string;
  pin: string;
};
type GiftCardBalanceResponseData = {
  voucherNumber: string;
  expiryDate: string;
  originalAmount: string;
  usedAmount: string;
  availableAmount: string;
};

type GiftCardBalanceResponse = {
  status: string;
  data: GiftCardBalanceResponseData;
};

async function getGiftCardBalance(data: GiftCardBalanceData): Promise<GiftCardBalanceResponseData> {
  /** Call DotApparel end point to check balance */
  /** Trigger modal window #gift_card_balance */

  const response = await fetch(`${window.location.origin}/apps/dotapparel/api-checkGiftcardBalance`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...data, type: "givex" }),
  });

  if (!response.ok) throw "Error";

  const giftCardBalanceResponse = (await response.json()) as GiftCardBalanceResponse;
  if (giftCardBalanceResponse.status !== "success") throw "Error";

  window.location.href = `${window.location.href}#gift_card_balance`;

  /** Return response data */
  /** Test data */
  return giftCardBalanceResponse.data;
}

export const ACTION_MAP = {
  [CART_EVENTS.ADD]: addToCart,
  [CART_EVENTS.ADD_GIFT_CARD]: addGiftCardToCart,
  [CART_EVENTS.ADD_LOOK_ITEM]: addLookItem,
  [CART_EVENTS.ADD_LOOK_ITEMS]: addLookItems,
  [CUSTOMER_EVENTS.UPDATE_DETAILS]: updateCustomerDetails,
  [CUSTOMER_EVENTS.RECOVER]: recoverCustomer,
  [CUSTOMER_EVENTS.UPDATE_LOGIN]: updateCustomerLogin,
  [CUSTOMER_EVENTS.UPDATE_PREFERENCES]: updateCustomerPreferences,
  [CUSTOMER_EVENTS.GIFT_CARD_BALANCE]: getGiftCardBalance,
  [KLAVIYO_EVENTS.ALERT]: klaviyoBackInStockNotify,
  "address:validation": "",
};
